import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

export default function WooCommerceLab() {
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [apiVersion, setApiVersion] = useState('wc/v3');
  const [endpoint, setEndpoint] = useState('system_status');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [useProxy, setUseProxy] = useState(true);
  const [fieldsMode, setFieldsMode] = useState('all'); // 'all' | 'ids'

  // When switching to customers endpoint, default to IDs-only to avoid PII
  useEffect(() => {
    if (endpoint === 'customers' && fieldsMode !== 'ids') {
      setFieldsMode('ids');
    }
  }, [endpoint]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('wooLab') || '{}');
      if (saved.storeUrl) setStoreUrl(saved.storeUrl);
      if (saved.consumerKey) setConsumerKey(saved.consumerKey);
      if (saved.consumerSecret) setConsumerSecret(saved.consumerSecret);
      if (saved.apiVersion) setApiVersion(saved.apiVersion);
      if (saved.endpoint) setEndpoint(saved.endpoint);
      if (saved.since) setSince(saved.since);
      if (saved.until) setUntil(saved.until);
      if (saved.page) setPage(saved.page);
      if (saved.perPage) setPerPage(saved.perPage);
    } catch {}
  }, []);

  useEffect(() => {
    const toSave = {
      storeUrl,
      consumerKey,
      consumerSecret,
      apiVersion,
      endpoint,
      since,
      until,
      page,
      perPage
    };
    try {
      localStorage.setItem('wooLab', JSON.stringify(toSave));
    } catch {}
  }, [storeUrl, consumerKey, consumerSecret, apiVersion, endpoint, since, until, page, perPage]);

  const base = useMemo(() => {
    let u = (storeUrl || '').trim();
    if (!u) return '';
    if (u.endsWith('/')) u = u.slice(0, -1);
    return `${u}/wp-json/${apiVersion}`;
  }, [storeUrl, apiVersion]);

  const buildUrlAndParams = () => {
    let path = '';
    const params = {};
    if (endpoint === 'system_status') {
      path = '/system_status';
    } else if (endpoint === 'orders' || endpoint === 'orderdetailv2') {
      path = '/orders';
      if (perPage) params.per_page = perPage;
      if (page) params.page = page;
      if (since) params.after = new Date(since).toISOString();
      if (until) params.before = new Date(until).toISOString();
      params.orderby = 'date';
      params.order = 'desc';
    } else if (endpoint === 'products') {
      path = '/products';
      if (perPage) params.per_page = perPage;
      if (page) params.page = page;
      params.orderby = 'date';
      params.order = 'desc';
    } else if (endpoint === 'customers') {
      path = '/customers';
      if (perPage) params.per_page = perPage;
      if (page) params.page = page;
      params.orderby = 'date';
      params.order = 'desc';
    }
    return { url: `${base}${path}`, params };
  };

  const callApi = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      if (!base) throw new Error('Store URL gerekli');
      if (!/^https:\/\//i.test(storeUrl || '')) {
        throw new Error('HTTPS zorunlu: Lütfen Store URL’i https ile girin');
      }
      if (!consumerKey || !consumerSecret) throw new Error('Consumer Key/Secret gerekli');
      const { url, params } = buildUrlAndParams();
      let resp;
      if (useProxy) {
        // Backend proxy üzerinden (CORS/mixed-content engellerini aşar ve loglar)
        const apiBase = 'https://api.glowytics.com/api/woocommerce';
        const route =
          endpoint === 'system_status'
            ? 'system-status'
            : (endpoint === 'orders' || endpoint === 'orderdetailv2')
            ? 'orders'
            : 'list';
        const proxiedParams =
          route === 'orders'
            ? { storeUrl, apiVersion, since, until, page, per_page: perPage }
            : route === 'system-status'
            ? { storeUrl, apiVersion }
            : { storeUrl, apiVersion, resource: endpoint, page, per_page: perPage };
        // _fields ile sadece ID çekme desteği
        if (fieldsMode === 'ids') {
          if (endpoint === 'customers') {
            proxiedParams._fields = 'id';
            proxiedParams.idsOnly = true;
          } else if (endpoint === 'orders' || endpoint === 'orderdetailv2') {
            // siparişten kullanıcı ID'si: customer_id
            proxiedParams._fields = 'id,customer_id';
            proxiedParams.idsOnly = true;
          }
        }
        resp = await axios.get(`${apiBase}/${route}`, {
          params: proxiedParams,
          headers: {
            'x-woo-ck': consumerKey,
            'x-woo-cs': consumerSecret
          },
          withCredentials: true
        });
      } else {
        // Doğrudan mağazaya çağrı (CORS ve mixed-content engeli olabilir)
        const directParams = { ...params };
        if (fieldsMode === 'ids') {
          if (endpoint === 'customers') {
            directParams._fields = 'id';
          } else if (endpoint === 'orders' || endpoint === 'orderdetailv2') {
            directParams._fields = 'id,customer_id';
          }
        }
        resp = await axios.get(url, {
          auth: { username: consumerKey, password: consumerSecret },
          params: directParams
        });
      }
      setResult(resp.data);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Unknown error';
      setError(`${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setEndpoint('system_status');
    await callApi();
  };

  const renderTable = () => {
    if (!result) return null;
    if (Array.isArray(result) && result.length > 0) {
      const first = result[0];
      const cols = Object.keys(first).slice(0, 12);
      return (
        <div className="overflow-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-medium text-gray-600 border-b">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.map((row, idx) => (
                <tr key={idx} className="odd:bg-white even:bg-gray-50">
                  {cols.map((c) => (
                    <td key={c} className="px-3 py-2 border-b truncate max-w-[280px]">
                      {typeof row[c] === 'object' ? JSON.stringify(row[c]) : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  const ordersPreview = useMemo(() => {
    if (endpoint !== 'orders') return [];
    const arr = Array.isArray(result) ? result : [];
    const deriveCustomerId = (o) => {
      const direct = Number(o?.customer_id || 0);
      if (direct && direct !== 0) return direct;
      const meta = Array.isArray(o?.meta_data) ? o.meta_data : [];
      const keys = ['_wpm_customer_user', '_customer_user', 'customer_user', '_wc_customer_user'];
      for (const k of keys) {
        const m = meta.find(x => String(x?.key || '').toLowerCase() === k.toLowerCase());
        if (m && m.value !== undefined && m.value !== null && String(m.value).trim() !== '') {
          const v = Number(m.value);
          if (Number.isFinite(v) && v > 0) return v;
        }
      }
      return 0;
    };
    return arr.slice(0, 50).map((o) => ({
      id: o?.id ?? '',
      date: o?.date_created ?? o?.date_created_gmt ?? '',
      total: o?.total ?? '',
      currency: o?.currency ?? '',
      customer_id: deriveCustomerId(o)
    }));
  }, [result, endpoint]);

  const orderItemsPreview = useMemo(() => {
    if (endpoint !== 'orderdetailv2') return [];
    const arr = Array.isArray(result) ? result : [];
    const rows = [];
    const deriveCustomerId = (o) => {
      const direct = Number(o?.customer_id || 0);
      if (direct && direct !== 0) return direct;
      const meta = Array.isArray(o?.meta_data) ? o.meta_data : [];
      const keys = ['_wpm_customer_user', '_customer_user', 'customer_user', '_wc_customer_user'];
      for (const k of keys) {
        const m = meta.find(x => String(x?.key || '').toLowerCase() === k.toLowerCase());
        if (m && m.value !== undefined && m.value !== null && String(m.value).trim() !== '') {
          const v = Number(m.value);
          if (Number.isFinite(v) && v > 0) return v;
        }
      }
      return 0;
    };
    for (const o of arr) {
      const orderId = o?.id ?? '';
      const customerId = deriveCustomerId(o);
      const orderDate = o?.date_created ?? o?.date_created_gmt ?? null;
      const currency = o?.currency ?? null;
      const items = Array.isArray(o?.line_items) ? o.line_items : [];
      for (const it of items) {
        const skuFromMeta = (() => {
          const meta = Array.isArray(it?.meta_data) ? it.meta_data : [];
          const keys = ['_sku', 'sku', 'SKU'];
          for (const k of keys) {
            const m = meta.find(x => String(x?.key || '').toLowerCase() === k.toLowerCase());
            if (m && m.value) return String(m.value);
          }
          return '';
        })();
        const categoryFromMeta = (() => {
          const meta = Array.isArray(it?.meta_data) ? it.meta_data : [];
          const keys = ['categories', 'category', 'product_cat', 'product_category', 'Category'];
          for (const k of keys) {
            const m = meta.find(x => String(x?.key || '').toLowerCase() === k.toLowerCase());
            if (!m) continue;
            const v = m.value;
            if (Array.isArray(v)) {
              return v.map(x => (x?.name || x?.slug || String(x))).join(', ');
            }
            if (typeof v === 'object' && v) {
              return v.name || v.slug || '';
            }
            return String(v);
          }
          return '';
        })();
        rows.push({
          order_id: orderId,
          customer_id: customerId,
          item_name: it?.name ?? '',
          price: it?.price ?? it?.subtotal ?? '',
          quantity: it?.quantity ?? '',
          line_total: it?.total ?? (Number(it?.price || 0) * Number(it?.quantity || 0)),
          item_id: it?.product_id ?? it?.variation_id ?? it?.id ?? '',
          sku: it?.sku ?? skuFromMeta,
          category: categoryFromMeta,
          order_date: orderDate,
          currency
        });
      }
    }
    return rows.slice(0, 200);
  }, [result, endpoint]);

  // Enrich categories by fetching product details via proxy (only for orderdetailv2)
  useEffect(() => {
    const enrichCategories = async () => {
      try {
        if (endpoint !== 'orderdetailv2') return;
        const orders = Array.isArray(result) ? result : [];
        if (orders.length === 0) return;
        const productIdSet = new Set();
        for (const o of orders) {
          const items = Array.isArray(o?.line_items) ? o.line_items : [];
          for (const it of items) {
            const pid = it?.product_id || it?.variation_id;
            if (pid) productIdSet.add(pid);
          }
        }
        const ids = Array.from(productIdSet);
        if (ids.length === 0) return;
        const brandId = localStorage.getItem('currentBrandId');
        const apiBase = 'https://api.glowytics.com/api/woocommerce';
        const chunkSize = 80; // Woo limit 100
        const idToCats = {};
        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);
          const resp = await axios.get(`${apiBase}/list`, {
            params: {
              storeUrl,
              apiVersion,
              resource: 'products',
              include: chunk.join(','),
              _fields: 'id,categories',
              status: 'any'
            },
            headers: {
              'x-woo-ck': consumerKey,
              'x-woo-cs': consumerSecret
            },
            withCredentials: true
          });
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          for (const p of rows) {
            const cid = p?.id;
            const cats = Array.isArray(p?.categories) ? p.categories.map(c => c?.name || c?.slug || '').filter(Boolean) : [];
            if (cid) idToCats[cid] = cats.join(', ');
          }
        }
        // Patch orderItemsPreview in-place by augmenting result (shallow)
        // We don't want to re-shape result; instead, map categories later in render using idToCats
        setCategoryMap(idToCats);
      } catch (e) {
        // ignore enrichment errors
      }
    };
    enrichCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, result, storeUrl, apiVersion, consumerKey, consumerSecret]);

  const [categoryMap, setCategoryMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [ordersSaving, setOrdersSaving] = useState(false);
  const [ordersSaveMsg, setOrdersSaveMsg] = useState('');
  const [scriptMode, setScriptMode] = useState('full'); // full | yesterday | custom
  const [scriptMsg, setScriptMsg] = useState('');
  const [scriptBusy, setScriptBusy] = useState(false);
  const [scriptOrdersMsg, setScriptOrdersMsg] = useState('');
  const [scriptOrdersBusy, setScriptOrdersBusy] = useState(false);

  const saveOrderDetailToDb = async () => {
    try {
      setSaving(true);
      setSaveMsg('');
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setSaveMsg('Brand ID bulunamadı (currentBrandId).');
        return;
      }
      const apiBase = 'https://api.glowytics.com/api/woocommerce';
      const rows = orderItemsPreview.map(r => ({
        order_id: Number(r.order_id) || null,
        customer_id: Number(r.customer_id) || null,
        item_id: Number(r.item_id) || null,
        sku: r.sku || null,
        item_name: r.item_name || null,
        item_category: categoryMap[r.item_id] || r.category || null,
        price: r.price != null ? Number(r.price) : null,
        quantity: r.quantity != null ? Number(r.quantity) : null,
        line_total: r.line_total != null ? Number(r.line_total) : null,
        currency: r.currency || null,
        order_date: r.order_date || null
      }));
      const resp = await axios.post(`${apiBase}/save-orderdetailv2`, { rows }, {
        headers: {
          'x-brand-id': brandId
        },
        withCredentials: true
      });
      setSaveMsg(`Saved: ${resp?.data?.inserted || 0}/${rows.length}`);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Unknown error';
      setSaveMsg(`Hata: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const saveOrdersToDb = async () => {
    try {
      setOrdersSaving(true);
      setOrdersSaveMsg('');
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setOrdersSaveMsg('Brand ID bulunamadı (currentBrandId).');
        return;
      }
      const apiBase = 'https://api.glowytics.com/api/woocommerce';
      const rows = ordersPreview.map(o => ({
        order_id: Number(o.id) || null,
        customer_id: Number(o.customer_id) || null,
        order_date: o.date || null,
        total: o.total != null ? Number(o.total) : null,
        currency: o.currency || null
      }));
      const resp = await axios.post(`${apiBase}/save-orders-min`, { rows }, {
        headers: { 'x-brand-id': brandId },
        withCredentials: true
      });
      setOrdersSaveMsg(`Saved: ${resp?.data?.upserted || 0}/${rows.length}`);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Unknown error';
      setOrdersSaveMsg(`Hata: ${msg}`);
    } finally {
      setOrdersSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <img 
              src="/woocommerceicon.png" 
              alt="WooCommerce" 
              className="w-8 h-8 object-contain"
              onError={(e) => { e.currentTarget.src = '/6033716.png'; }}
            />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">WooCommerce Lab</h1>
            <p className="text-sm text-gray-500 mt-1">Direct API test with manual credentials</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 text-sm bg-gray-50 text-gray-700 rounded-xl border hover:bg-gray-100"
            onClick={testConnection}
            disabled={loading}
          >
            {loading && endpoint === 'system_status' ? 'Testing…' : 'Test Connection'}
          </button>
          <button
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700"
            onClick={callApi}
            disabled={loading}
          >
            {loading ? 'Fetching…' : 'Fetch'}
          </button>
          {endpoint === 'orderdetailv2' && orderItemsPreview.length > 0 && (
            <button
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:bg-gray-300"
              onClick={saveOrderDetailToDb}
              disabled={saving}
              title="Order detail rows will be saved to brand schema"
            >
              {saving ? 'Saving…' : 'Save to DB'}
            </button>
          )}
          {endpoint === 'orders' && ordersPreview.length > 0 && (
            <button
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:bg-gray-300"
              onClick={saveOrdersToDb}
              disabled={ordersSaving}
              title="Orders will be saved to brand schema (woo_orders_min_raw)"
            >
              {ordersSaving ? 'Saving…' : 'Save Orders to DB'}
            </button>
          )}
          {/* Script controls */}
          <select
            className="px-3 py-2 text-sm border rounded-xl"
            value={scriptMode}
            onChange={(e) => setScriptMode(e.target.value)}
            title="Script date mode"
          >
            <option value="full">Script: Full</option>
            <option value="yesterday">Script: Yesterday</option>
            <option value="custom">Script: Custom</option>
          </select>
          <button
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:bg-gray-300"
            onClick={async () => {
              try {
                setScriptBusy(true);
                setScriptMsg('');
                const brandId = localStorage.getItem('currentBrandId');
                if (!brandId) throw new Error('Brand ID not found');
                const payload = {
                  mode: scriptMode,
                  startDate: scriptMode === 'custom' ? (since || undefined) : undefined,
                  endDate: scriptMode === 'custom' ? (until || undefined) : undefined,
                  perPage: 100,
                  maxPages: 300
                };
                const resp = await axios.post('https://api.glowytics.com/api/labs/woocommerce/ingest-orderdetail', payload, {
                  headers: { 'x-brand-id': brandId },
                  withCredentials: true
                });
                setScriptMsg(`OrderDetail Script OK: inserted=${resp?.data?.inserted ?? resp?.data?.rowsInserted ?? 'n/a'}`);
              } catch (e) {
                const msg = e?.response?.data?.error || e?.message || 'Unknown error';
                setScriptMsg(`OrderDetail Script ERR: ${msg}`);
              } finally {
                setScriptBusy(false);
              }
            }}
            disabled={scriptBusy}
            title="Run script to ingest order items (woo_orderdetail_v2_raw)"
          >
            {scriptBusy ? 'Running…' : 'Run OrderDetail Script'}
          </button>
          <button
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:bg-gray-300"
            onClick={async () => {
              try {
                setScriptOrdersBusy(true);
                setScriptOrdersMsg('');
                const brandId = localStorage.getItem('currentBrandId');
                if (!brandId) throw new Error('Brand ID not found');
                const payload = {
                  mode: scriptMode,
                  startDate: scriptMode === 'custom' ? (since || undefined) : undefined,
                  endDate: scriptMode === 'custom' ? (until || undefined) : undefined,
                  perPage: 100,
                  maxPages: 300
                };
                const resp = await axios.post('https://api.glowytics.com/api/labs/woocommerce/ingest-orders-min', payload, {
                  headers: { 'x-brand-id': brandId },
                  withCredentials: true
                });
                setScriptOrdersMsg(`Orders Script OK: upserted=${resp?.data?.upserted ?? 'n/a'}`);
              } catch (e) {
                const msg = e?.response?.data?.error || e?.message || 'Unknown error';
                setScriptOrdersMsg(`Orders Script ERR: ${msg}`);
              } finally {
                setScriptOrdersBusy(false);
              }
            }}
            disabled={scriptOrdersBusy}
            title="Run script to ingest orders (woo_orders_min_raw)"
          >
            {scriptOrdersBusy ? 'Running…' : 'Run Orders Script'}
          </button>
        </div>
      </div>

      <div className="bg-white p-5 rounded-2xl shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Store URL</label>
            <input
              type="text"
              className="w-full border rounded-xl px-3 py-2"
              placeholder="https://example.com"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">API Version</label>
            <select
              className="w-full border rounded-xl px-3 py-2"
              value={apiVersion}
              onChange={(e) => setApiVersion(e.target.value)}
            >
              <option value="wc/v3">wc/v3</option>
              <option value="wc/v2">wc/v2</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Call Mode</label>
            <select
              className="w-full border rounded-xl px-3 py-2"
              value={useProxy ? 'proxy' : 'direct'}
              onChange={(e) => setUseProxy(e.target.value === 'proxy')}
            >
              <option value="proxy">Backend Proxy (önerilen)</option>
              <option value="direct">Direct (tarayıcıdan)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Endpoint</label>
            <select
              className="w-full border rounded-xl px-3 py-2"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
            >
              <option value="system_status">system_status</option>
              <option value="orders">orders</option>
              <option value="orderdetailv2">orderdetailv2</option>
              <option value="products">products</option>
              <option value="customers">customers</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Fields</label>
            <select
              className="w-full border rounded-xl px-3 py-2"
              value={fieldsMode}
              onChange={(e) => setFieldsMode(e.target.value)}
            >
              <option value="all">All (PII içerebilir)</option>
              <option value="ids">Only IDs (önerilen)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Consumer Key</label>
            <input
              type="password"
              className="w-full border rounded-xl px-3 py-2"
              value={consumerKey}
              onChange={(e) => setConsumerKey(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Consumer Secret</label>
            <input
              type="password"
              className="w-full border rounded-xl px-3 py-2"
              value={consumerSecret}
              onChange={(e) => setConsumerSecret(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Since</label>
              <input
                type="date"
                className="w-full border rounded-xl px-3 py-2"
                value={since}
                onChange={(e) => setSince(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Until</label>
              <input
                type="date"
                className="w-full border rounded-xl px-3 py-2"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Page</label>
              <input
                type="number"
                min={1}
                className="w-full border rounded-xl px-3 py-2"
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Per Page</label>
              <input
                type="number"
                min={1}
                max={100}
                className="w-full border rounded-xl px-3 py-2"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-xl border border-red-100">
            {error}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Simple Orders Preview */}
        {ordersPreview.length > 0 && (
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <div className="text-sm font-medium text-gray-800 mb-2">Orders (basic fields)</div>
            {scriptOrdersMsg && (
              <div className="mb-2 text-xs px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-700 inline-block">
                {scriptOrdersMsg}
              </div>
            )}
            {ordersSaveMsg && (
              <div className="mb-2 text-xs px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-700 inline-block">
                {ordersSaveMsg}
              </div>
            )}
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Order ID</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Customer ID</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Date</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Total</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersPreview.map((r, i) => (
                    <tr key={i} className="odd:bg-white even:bg-gray-50">
                      <td className="px-3 py-2 border-b">{r.id}</td>
                      <td className="px-3 py-2 border-b">{r.customer_id}</td>
                      <td className="px-3 py-2 border-b">{String(r.date).replace('T', ' ')}</td>
                      <td className="px-3 py-2 border-b">{r.total}</td>
                      <td className="px-3 py-2 border-b">{r.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Order Items Preview */}
        {orderItemsPreview.length > 0 && (
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <div className="text-sm font-medium text-gray-800 mb-2">Order Items (price & quantity)</div>
            {scriptMsg && (
              <div className="mb-2 text-xs px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-700 inline-block">
                {scriptMsg}
              </div>
            )}
            {saveMsg && (
              <div className="mb-2 text-xs px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-700 inline-block">
                {saveMsg}
              </div>
            )}
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Order ID</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Customer ID</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Item ID</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">SKU</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Item</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Category</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Price</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Qty</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 border-b">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orderItemsPreview.map((r, i) => (
                    <tr key={i} className="odd:bg-white even:bg-gray-50">
                      <td className="px-3 py-2 border-b">{r.order_id}</td>
                      <td className="px-3 py-2 border-b">{r.customer_id}</td>
                      <td className="px-3 py-2 border-b">{r.item_id}</td>
                      <td className="px-3 py-2 border-b">{r.sku}</td>
                      <td className="px-3 py-2 border-b">{r.item_name}</td>
                      <td className="px-3 py-2 border-b">{categoryMap[r.item_id] || r.category}</td>
                      <td className="px-3 py-2 border-b">{r.price}</td>
                      <td className="px-3 py-2 border-b">{r.quantity}</td>
                      <td className="px-3 py-2 border-b">{r.line_total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Table preview for common list endpoints */}
        {renderTable()}

        {/* Raw JSON */}
        <div className="bg-white p-4 rounded-2xl shadow-sm">
          <div className="text-sm text-gray-600 mb-2">Raw Response</div>
          <pre className="text-xs bg-gray-50 p-3 rounded-xl overflow-auto max-h-[480px]">
{JSON.stringify(result, null, 2)}
          </pre>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Not: Tarayıcıdan direkt WooCommerce API çağrıları CORS tarafından engellenebilir. Bu durumda backend proxy gerekebilir.
      </div>
    </div>
  );
}


